/**
 * Spatial attention workspace (Wave 2.10).
 * Attention = review state; spatial events remain immutable evidence.
 * CTAs open existing authorities — no hidden mutation.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import pmvService from '@/services/pmv.service';
import { useAuthStore } from '@/store/authStore';
import { formatLastSeen } from '@/utils/pmvConnectedCopy';

const { Text } = Typography;

export const PmvSpatialAttentionPage: React.FC = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.is_super_admin ||
      user?.permissions?.includes('pmv.connected.reconciliation.manage') ||
      user?.permissions?.includes('pmv.admin') ||
      user?.permissions?.includes('pmv.manage')
  );

  const [status, setStatus] = useState<string | undefined>('OPEN');
  const [caseType, setCaseType] = useState<string | undefined>(() => {
    const t = new URLSearchParams(window.location.search).get('type');
    return t || undefined;
  });
  const [q, setQ] = useState('');

  const listQ = useQuery({
    queryKey: ['pmv-spatial-attention', status],
    queryFn: () => pmvService.listSpatialAttention({ status, limit: 200 }),
    refetchInterval: 60_000,
  });

  const promoteMut = useMutation({
    mutationFn: (id: string) => pmvService.promoteSpatialAttention(id),
    onSuccess: (data) => {
      message.success('Promoted — open the linked PMV workflow');
      void qc.invalidateQueries({ queryKey: ['pmv-spatial-attention'] });
      if (data.authoritative_action?.path) navigate(data.authoritative_action.path);
    },
    onError: (e: Error) => message.error(e.message || 'Promote failed'),
  });

  const dismissMut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      pmvService.dismissReconciliationCase(id, reason),
    onSuccess: () => {
      message.success('Dismissed');
      void qc.invalidateQueries({ queryKey: ['pmv-spatial-attention'] });
    },
    onError: (e: Error) => message.error(e.message || 'Dismiss failed'),
  });

  const rows = (listQ.data ?? []).filter((r) => {
    if (caseType && r.case_type !== caseType) return false;
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      return (
        r.pmv_profile_id.toLowerCase().includes(needle) ||
        (r.detail || '').toLowerCase().includes(needle) ||
        r.case_type.toLowerCase().includes(needle)
      );
    }
    return true;
  });

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Spatial attention"
          documentNumber=""
          statusTag={<Tag color="orange">Evidence review</Tag>}
          subtitle={
            <Text type="secondary">
              Advisory only — does not change allocation, ops status, or meters. Events stay immutable.
            </Text>
          }
          primaryActions={
            <Space wrap>
              <Button onClick={() => navigate('/asset/pmv/connected/map')}>Live Fleet Map</Button>
              <Button onClick={() => navigate('/asset/pmv/connected/reconciliation')}>
                Reconciliation
              </Button>
              <Button onClick={() => navigate('/asset/pmv/connected')}>Connected overview</Button>
            </Space>
          }
        />
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          type="info"
          showIcon
          message="Human action opens existing authorities"
          description="Review Allocation · Open Equipment · Open Project · Review Device — never silent mutation from this screen."
        />
        <Card size="small">
          <Space wrap style={{ marginBottom: 12 }}>
            <Select
              allowClear
              placeholder="Status"
              style={{ width: 140 }}
              value={status}
              onChange={(v) => setStatus(v)}
              options={[
                { value: 'OPEN', label: 'Open' },
                { value: 'PROMOTED', label: 'Promoted' },
                { value: 'DISMISSED', label: 'Dismissed' },
              ]}
            />
            <Select
              allowClear
              placeholder="Type"
              style={{ width: 220 }}
              value={caseType}
              onChange={(v) => setCaseType(v)}
              options={[
                { value: 'LOCATION_CONTEXT_MISMATCH', label: 'Location mismatch' },
                { value: 'UNEXPECTED_MOVEMENT', label: 'Unexpected movement' },
                { value: 'RESTRICTED_AREA', label: 'Restricted area' },
              ]}
            />
            <Input.Search
              allowClear
              placeholder="Equipment / detail"
              style={{ width: 220 }}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </Space>
          <Table
            size="small"
            rowKey="id"
            loading={listQ.isLoading}
            dataSource={rows}
            pagination={{ pageSize: 20 }}
            locale={{ emptyText: 'No spatial attention cases' }}
            columns={[
              {
                title: 'Type',
                dataIndex: 'case_type',
                width: 180,
                render: (v: string) => <Tag>{v.replace(/_/g, ' ')}</Tag>,
              },
              {
                title: 'Severity',
                dataIndex: 'severity',
                width: 100,
                render: (v: string) => (
                  <Tag color={v === 'HIGH' ? 'error' : 'warning'}>{v}</Tag>
                ),
              },
              {
                title: 'Equipment',
                dataIndex: 'pmv_profile_id',
                ellipsis: true,
                render: (v: string) => (
                  <Button type="link" onClick={() => navigate(`/asset/pmv/${v}`)}>
                    {v.slice(0, 8)}…
                  </Button>
                ),
              },
              { title: 'Detail', dataIndex: 'detail', ellipsis: true },
              {
                title: 'Updated',
                dataIndex: 'updated_at',
                width: 120,
                render: (v: string | undefined, r) => formatLastSeen(v || r.created_at),
              },
              {
                title: 'Actions',
                key: 'actions',
                width: 260,
                render: (_: unknown, r) => (
                  <Space wrap size="small">
                    <Button size="small" onClick={() => navigate(`/asset/pmv/${r.pmv_profile_id}`)}>
                      Open equipment
                    </Button>
                    <Button
                      size="small"
                      onClick={() => navigate('/asset/pmv/connected/map')}
                    >
                      Map
                    </Button>
                    {canManage && r.status === 'OPEN' ? (
                      <>
                        <Button
                          size="small"
                          type="primary"
                          loading={promoteMut.isPending}
                          onClick={() => promoteMut.mutate(r.id)}
                        >
                          Promote
                        </Button>
                        <Button
                          size="small"
                          danger
                          loading={dismissMut.isPending}
                          onClick={() =>
                            dismissMut.mutate({
                              id: r.id,
                              reason: 'Reviewed on spatial attention workspace',
                            })
                          }
                        >
                          Dismiss
                        </Button>
                      </>
                    ) : null}
                  </Space>
                ),
              },
            ]}
          />
        </Card>
      </Space>
    </WorkspaceLayout>
  );
};

export default PmvSpatialAttentionPage;
